import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Briefcase, Calculator, ExternalLink, Plug, Copy, Check, AlertTriangle } from "lucide-react";

export default function AdminIntegrations() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () =>
    api.get("/integrations/status").then((r) => {
      setStatus(r.data);
      setLoading(false);
    });

  useEffect(() => {
    load();
    const connected = searchParams.get("connected");
    if (connected) {
      toast.success(`${connected === "jobber" ? "Jobber" : "QuickBooks Online"} connected successfully.`);
      searchParams.delete("connected");
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line

  const connect = async (service) => {
    setBusy(service);
    try {
      const { data } = await api.get(`/integrations/${service}/connect`);
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy("");
    }
  };

  const disconnect = async (service) => {
    if (!window.confirm(`Disconnect ${service === "jobber" ? "Jobber" : "QuickBooks"}? Existing synced records stay intact.`)) return;
    setBusy(service);
    try {
      await api.post(`/integrations/${service}/disconnect`);
      toast.success("Disconnected.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  if (loading) return <div className="text-slate-400">Loading integrations…</div>;

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Integrations</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tighter font-medium mt-2">
          External services
        </h1>
        <p className="mt-3 text-sm text-slate-600 max-w-2xl">
          Connect Mead&apos;s portal to your operational tools. Push work orders to
          Jobber, sync invoices to QuickBooks Online.
        </p>
      </div>

      <IntegrationCard
        service="jobber"
        icon={Briefcase}
        title="Jobber"
        subtitle="Work order & job management"
        state={status.jobber}
        busy={busy === "jobber"}
        onConnect={() => connect("jobber")}
        onDisconnect={() => disconnect("jobber")}
        setupHref="https://developer.getjobber.com/docs/getting_started/"
        setupSteps={[
          "Create an app in the Jobber Developer Center (developer.getjobber.com).",
          "Copy the Client ID and Client Secret from your app's Keys section.",
          "Register the OAuth callback URL shown below in your Jobber app settings.",
          "Add JOBBER_CLIENT_ID, JOBBER_CLIENT_SECRET, and JOBBER_REDIRECT_URI to backend/.env.",
          "Restart the backend, then click Connect Jobber to complete the OAuth flow.",
        ]}
      />

      <IntegrationCard
        service="qbo"
        icon={Calculator}
        title="QuickBooks Online"
        subtitle="Invoicing & accounting sync"
        state={status.qbo}
        busy={busy === "qbo"}
        onConnect={() => connect("qbo")}
        onDisconnect={() => disconnect("qbo")}
        setupHref="https://developer.intuit.com/app/developer/qbo/docs/develop"
        setupSteps={[
          "Create an app in the Intuit Developer Portal (developer.intuit.com).",
          "Ensure the app targets the QuickBooks Online Accounting API.",
          "Register the OAuth Redirect URI shown below in the app's Keys section.",
          "Copy the Client ID + Client Secret (Sandbox for testing, Production for live).",
          "Set QBO_CLIENT_ID, QBO_CLIENT_SECRET, QBO_REDIRECT_URI, and QBO_ENVIRONMENT in backend/.env.",
          "Restart the backend, then click Connect QuickBooks to authorize your company.",
        ]}
      />
    </div>
  );
}

function IntegrationCard({ service, icon: Icon, title, subtitle, state, busy, onConnect, onDisconnect, setupHref, setupSteps }) {
  const [copied, setCopied] = useState(false);

  const copyRedirect = async () => {
    const val = state.redirect_uri || `${process.env.REACT_APP_BACKEND_URL}/api/integrations/${service}/callback`;
    try {
      await navigator.clipboard.writeText(val);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed — please select the URL and copy manually.");
    }
  };

  const suggestedRedirect = `${process.env.REACT_APP_BACKEND_URL}/api/integrations/${service}/callback`;

  return (
    <div className="bg-white border border-slate-200" data-testid={`integration-card-${service}`}>
      <div className="p-6 flex items-start gap-5 border-b border-slate-100">
        <div className="h-12 w-12 bg-[#0B192C] text-white flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-2xl tracking-tighter">{title}</h2>
            <StatusPill state={state} />
          </div>
          <div className="text-sm text-slate-500 mt-0.5">
            {subtitle} · Environment: <span className="font-mono-alt">{state.environment}</span>
            {state.realm_id && <> · Realm: <span className="font-mono-alt">{state.realm_id}</span></>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {state.connected ? (
            <button
              data-testid={`disconnect-${service}`}
              onClick={onDisconnect}
              disabled={busy}
              className="px-4 py-2 border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50 rounded-sm disabled:opacity-60"
            >
              Disconnect
            </button>
          ) : (
            <button
              data-testid={`connect-${service}`}
              onClick={onConnect}
              disabled={busy || !state.configured}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-sm font-semibold rounded-sm disabled:opacity-40"
            >
              <Plug className="h-4 w-4" /> {busy ? "Redirecting…" : `Connect ${title}`}
            </button>
          )}
        </div>
      </div>

      {!state.configured && (
        <div className="p-6 bg-amber-50 border-b border-amber-200 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <div className="font-semibold">Configuration required</div>
            <div className="mt-1 text-amber-800">
              Credentials for {title} aren&apos;t set yet. Follow the setup steps
              below to add them to <code className="font-mono-alt">backend/.env</code>.
            </div>
          </div>
        </div>
      )}

      <div className="p-6 grid md:grid-cols-5 gap-6">
        <div className="md:col-span-2 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
            OAuth redirect URI
          </div>
          <div className="flex items-center gap-2 border border-slate-200 bg-slate-50 px-3 py-2 font-mono-alt text-xs break-all">
            <span className="flex-1">{state.redirect_uri || suggestedRedirect}</span>
            <button
              data-testid={`copy-redirect-${service}`}
              onClick={copyRedirect}
              className="p-1 hover:bg-white rounded-sm shrink-0"
              title="Copy"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </div>
          <div className="text-xs text-slate-500">
            {state.redirect_uri
              ? "Register this exact URL in the app's OAuth settings."
              : `Suggested. Set ${service.toUpperCase()}_REDIRECT_URI in backend/.env to this value, then restart the backend.`}
          </div>
          <a href={setupHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E40AF]">
            Developer docs <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        <div className="md:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">
            Setup steps
          </div>
          <ol className="space-y-2 text-sm text-slate-700 list-decimal list-inside">
            {setupSteps.map((step) => (
              <li key={step} className="leading-relaxed">{step}</li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ state }) {
  let label, cls;
  if (!state.configured) {
    label = "Not configured";
    cls = "bg-slate-100 text-slate-600 border-slate-300";
  } else if (state.connected) {
    label = "Connected";
    cls = "bg-emerald-100 text-emerald-900 border-emerald-300";
  } else {
    label = "Not connected";
    cls = "bg-amber-100 text-amber-900 border-amber-300";
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] border ${cls} rounded-sm`}>
      {label}
    </span>
  );
}
